<template>
  <div class="main-content">
    <!-- 第一级：标签栏（阶段/范围） -->
    <div class="stage-tabs">
      <div
        v-for="stageKey in stageKeys"
        :key="stageKey"
        class="stage-tab"
        :class="{ 'stage-tab-active': currentStageKey === stageKey }"
        @click="switchStage(stageKey)"
      >
        {{ emissionSourceConfig[stageKey]?.label }}
      </div>
    </div>

    <!-- 排放源内容区域 -->
    <div class="emission-content">
      <!-- 第二级：分类列表 -->
      <div
        v-for="(category, categoryKey) in currentStageCategoriesWithSources"
        :key="categoryKey"
        class="emission-category"
      >
        <!-- 分类标题（可折叠） -->
        <div
          class="category-header"
          :class="{ 'category-expanded': isCategoryExpanded(currentStageKey, categoryKey) }"
          @click="toggleCategory(currentStageKey, categoryKey)"
        >
          <div class="category-arrow">
            <img
              :src="
                isCategoryExpanded(currentStageKey, categoryKey)
                  ? require('@/assets/images/chatpreview/arrow-down.svg')
                  : require('@/assets/images/chatpreview/arrow-right.svg')
              "
              alt="arrow"
            />
          </div>
          <div class="category-label">{{ category.label }}</div>
        </div>

        <!-- 第三级：分类下的排放源列表 -->
        <div
          class="category-items"
          v-show="isCategoryExpanded(currentStageKey, categoryKey)"
        >
          <div
            v-for="(emissionSource, emissionSourceIndex) in getEmissionSources(category)"
            :key="emissionSourceIndex"
            class="emission-source-group"
          >
            <!-- 排放源名称（可折叠） -->
            <div
              class="emission-source-header"
              :class="{ 'emission-source-expanded': isEmissionSourceExpanded(currentStageKey, categoryKey, emissionSourceIndex) }"
              @click="toggleEmissionSource(currentStageKey, categoryKey, emissionSourceIndex)"
            >
              <div class="emission-source-arrow">
                <img
                  :src="
                    isEmissionSourceExpanded(currentStageKey, categoryKey, emissionSourceIndex)
                      ? require('@/assets/images/chatpreview/arrow-down.svg')
                      : require('@/assets/images/chatpreview/arrow-right_deep.svg')
                  "
                  alt="arrow"
                />
              </div>
              <div class="emission-source-label">{{ emissionSource.name }}</div>
            </div>

            <!-- 文件列表 -->
            <div
              class="emission-source-files"
              v-show="isEmissionSourceExpanded(currentStageKey, categoryKey, emissionSourceIndex)"
            >
              <div
                v-for="(file, fileIndex) in getEmissionSourceFiles(emissionSource)"
                :key="fileIndex"
                class="file-item"
              >
                <el-tooltip
                  :content="file.name"
                  placement="bottom"
                  effect="light"
                  :open-delay="300"
                  popper-class="chat-preview-tooltip"
                >
                  <div class="file-icon">
                    <img :src="getFileIcon(file.name)" alt="file" />
                  </div>
                  <!-- <div class="file-name">{{ file.name || `空文件` }}</div> -->
                </el-tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
export default {
  name: "EmissionSource",
  props: {
    previewData: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      // 第一级：当前选中的阶段/范围标签页
      currentStageKey: null,
      // 第二级：已展开的分类（按阶段分组）
      expandedCategories: {},
      // 第三级：已展开的排放源（按阶段和分类分组）
      expandedEmissionSources: {},
    };
  },
  computed: {
    ...mapState(["accessType", "userInfo"]),

    emissionSourceConfig() {
      // 如果 previewData 存在，优先使用预览数据，否则使用配置数据
      if (this.previewData) {
        return this.previewData;
      }
      return {};
    },
    // 第一级：获取所有阶段/范围标签页的键（按顺序），只返回有数据的阶段
    stageKeys() {
      const config = this.emissionSourceConfig;
      if (!config) return [];
      
      let keys = [];
      if (this.accessType === "tpc") {
        keys = ["scope_1", "scope_2", "scope_3"];
      } else if (this.accessType === "tzj") {
        keys = [
          "raw_material_acquisition_stage",
          "production_manufacturing_stage",
          "transportation_and_storage_stage",
        ];
      }
      
      // 过滤：只返回有实际数据的阶段（有分类且分类下有排放源）
      return keys.filter((key) => {
        const stage = config[key];
        if (!stage) return false;
        
        // 检查该阶段是否有分类和排放源数据
        const categories = this.getCategoriesForStage(stage);
        for (const categoryKey in categories) {
          const category = categories[categoryKey];
          const emissionSources = this.getEmissionSources(category);
          if (emissionSources && emissionSources.length > 0) {
            return true; // 找到有数据的分类，该阶段应该显示
          }
        }
        return false; // 没有找到有数据的分类，该阶段不显示
      });
    },
    // 第二级：获取当前阶段下有排放源的分类列表（过滤掉空分类）
    currentStageCategoriesWithSources() {
      const categories = this.getCurrentStageCategories();
      const filteredCategories = {};
      for (const categoryKey in categories) {
        const category = categories[categoryKey];
        const emissionSources = this.getEmissionSources(category);
        if (emissionSources && emissionSources.length > 0) {
          filteredCategories[categoryKey] = category;
        }
      }
      return filteredCategories;
    },
  },
  watch: {
    // 监听配置变化，自动初始化默认选中和展开状态
    emissionSourceConfig: {
      immediate: true,
      deep: true,
      handler(newConfig) {
        if (!newConfig || Object.keys(newConfig).length === 0) {
          return;
        }
        this.initializeDefaultState();
      },
    },
  },
  mounted() {
    // 组件挂载时，如果配置已存在，初始化默认状态
    if (this.emissionSourceConfig && Object.keys(this.emissionSourceConfig).length > 0) {
      this.initializeDefaultState();
    }
  },
  methods: {
    // 初始化默认状态（默认选中第一个阶段标签页）
    initializeDefaultState() {
      const config = this.emissionSourceConfig;
      if (!config || Object.keys(config).length === 0) {
        return;
      }

      // 第一级：获取第一个阶段标签页的键
      const firstStageKey = this.stageKeys[0];
      if (!firstStageKey) {
        return;
      }

      // 设置默认选中的阶段标签页
      if (this.currentStageKey !== firstStageKey) {
        this.currentStageKey = firstStageKey;
      }

      // 初始化所有阶段的展开状态
      this.stageKeys.forEach((stageKey) => {
        // 第二级：初始化分类展开状态
        if (!this.expandedCategories[stageKey]) {
          this.$set(this.expandedCategories, stageKey, []);
        }

        // 第三级：初始化排放源展开状态
        if (!this.expandedEmissionSources[stageKey]) {
          this.$set(this.expandedEmissionSources, stageKey, {});
        }

        // 获取当前阶段下的分类，并初始化排放源展开状态
        const stage = config[stageKey];
        if (stage) {
          const categories = this.getCategoriesForStage(stage);
          Object.keys(categories).forEach((categoryKey) => {
            if (!this.expandedEmissionSources[stageKey][categoryKey]) {
              this.$set(this.expandedEmissionSources[stageKey], categoryKey, []);
            }
          });
        }
      });
    },
    // 第二级：获取阶段下的分类列表（适配TPC和TZJ两种结构）
    getCategoriesForStage(stage) {
      if (!stage) return {};
      const categories = {};
      for (const key in stage) {
        // 排除 label 键
        if (key === "label") {
          continue;
        }
        
        const item = stage[key];
        // 只处理对象类型
        if (typeof item !== "object" || item === null) {
          continue;
        }
        
        // TZJ: 排除 value 是字符串的情况（如 raw_material_acquisition_stage.value = ""）
        // TPC: value 是数组，不会被排除
        if ("value" in item && typeof item.value === "string") {
          continue;
        }
        
        categories[key] = item;
      }
      return categories;
    },
    // 第一级：切换阶段/范围标签页
    switchStage(stageKey) {
      this.currentStageKey = stageKey;
      // 如果当前阶段没有展开的分类，初始化展开状态
      if (!this.expandedCategories[stageKey]) {
        this.$set(this.expandedCategories, stageKey, []);
      }
      if (!this.expandedEmissionSources[stageKey]) {
        this.$set(this.expandedEmissionSources, stageKey, {});
      }
    },
    // 第二级：获取当前阶段下的分类列表
    getCurrentStageCategories() {
      if (!this.currentStageKey) return {};
      const stage = this.emissionSourceConfig[this.currentStageKey];
      if (!stage) return {};
      return this.getCategoriesForStage(stage);
    },
    // 第三级：获取分类下的排放源列表
    getEmissionSources(category) {
      if (!category || !Array.isArray(category.value)) {
        return [];
      }
      return category.value;
    },
    // 获取排放源下的文件列表
    getEmissionSourceFiles(emissionSource) {
      if (!emissionSource || !Array.isArray(emissionSource.files)) {
        return [];
      }
      return emissionSource.files;
    },
    // 第二级：判断分类是否展开
    isCategoryExpanded(stageKey, categoryKey) {
      return (
        this.expandedCategories[stageKey] &&
        this.expandedCategories[stageKey].includes(categoryKey)
      );
    },
    // 第二级：切换分类展开/收起
    toggleCategory(stageKey, categoryKey) {
      if (!this.expandedCategories[stageKey]) {
        this.$set(this.expandedCategories, stageKey, []);
      }
      const categories = this.expandedCategories[stageKey];
      const index = categories.indexOf(categoryKey);
      if (index > -1) {
        categories.splice(index, 1);
      } else {
        categories.push(categoryKey);
      }
    },
    // 第三级：判断排放源是否展开
    isEmissionSourceExpanded(stageKey, categoryKey, emissionSourceIndex) {
      return (
        this.expandedEmissionSources[stageKey] &&
        this.expandedEmissionSources[stageKey][categoryKey] &&
        this.expandedEmissionSources[stageKey][categoryKey].includes(emissionSourceIndex)
      );
    },
    // 第三级：切换排放源展开/收起
    toggleEmissionSource(stageKey, categoryKey, emissionSourceIndex) {
      if (!this.expandedEmissionSources[stageKey]) {
        this.$set(this.expandedEmissionSources, stageKey, {});
      }
      if (!this.expandedEmissionSources[stageKey][categoryKey]) {
        this.$set(this.expandedEmissionSources[stageKey], categoryKey, []);
      }
      const emissionSources = this.expandedEmissionSources[stageKey][categoryKey];
      const index = emissionSources.indexOf(emissionSourceIndex);
      if (index > -1) {
        emissionSources.splice(index, 1);
      } else {
        emissionSources.push(emissionSourceIndex);
      }
    },
    // 根据文件扩展名获取对应的图标
    getFileIcon(fileName) {
      if (!fileName) {
        return require("@/assets/images/chatpreview/file.svg");
      }
      
      // 获取文件扩展名（转换为小写）
      const extension = fileName.split(".").pop()?.toLowerCase() || "";
      
      // 根据扩展名返回对应的图标
      switch (extension) {
        case "doc":
        case "docx":
          return require("@/assets/images/chatpreview/file_doc.svg");
        case "xls":
        case "xlsx":
          return require("@/assets/images/chatpreview/file_excel.svg");
        case "jpg":
        case "jpeg":
        case "png":
        case "gif":
        case "bmp":
        case "webp":
        case "svg":
        case "ico":
          return require("@/assets/images/chatpreview/file_img.svg");
        case "pdf":
          return require("@/assets/images/chatpreview/file_pdf.svg");
        case "ppt":
        case "pptx":
          return require("@/assets/images/chatpreview/file_ppt.svg");
        default:
          return require("@/assets/images/chatpreview/file.svg");
      }
    },
  },
};
</script>

<style scoped lang="scss">
.main-content {
  width: 100%;
  height: 100%;
  padding: 8px 12px;
}

// 第一级：阶段/范围标签页样式
.stage-tabs {
  display: flex;
  gap: 4px;
}

.stage-tab {
  padding: 8px 16px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(5, 32, 49, 1);
  cursor: pointer;
  border-radius: 4px;
}

.stage-tab-active {
  background-color: rgba(219, 232, 245, 1);
}

// 排放源内容区域
.emission-content {
  padding: 8px 0;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  /* Chrome, Safari, Edge */
  &::-webkit-scrollbar {
    display: none;
  }
}

.emission-category {
  width: 100%;
}

// 第二级：分类标题样式
.category-header {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  padding-left: 10px;

  &:hover {
    background: rgba(244, 248, 252, 1);
  }
}

.category-header.category-expanded {
  background: rgba(219, 232, 245, 1);
  border-radius: 8px;
}

.category-arrow {
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.category-label {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  color: rgba(0, 43, 37, 1);

  .category-header:not(.category-expanded) & {
    color: rgba(170, 179, 187, 1);
  }
}

// 第三级：分类下的排放源列表
.category-items {
  width: 100%;
  padding-left: 24px;
}

.emission-source-group {
  width: 100%;
  margin-bottom: 8px;
}

// 第三级：排放源名称样式
.emission-source-header {
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  padding-left: 10px;
  margin-top: 4px;

  // &:hover {
  //   background: rgba(243, 247, 250, 1);
  // }
}

// .emission-source-header.emission-source-expanded {
//   background: rgba(243, 247, 250, 1);
// }

.emission-source-arrow {
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.emission-source-label {
  flex: 1;
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
  color: rgba(0, 43, 37, 1);

  // .emission-source-header:not(.emission-source-expanded) & {
  //   color: rgba(176, 192, 191, 1);
  // }
}

// 文件列表样式
.emission-source-files {
  width: 100%;
  padding-left: 32px;
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  margin: 10px 0;
  flex-wrap: wrap;
}

.file-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  // cursor: pointer;
  // transition: transform 0.2s ease;
  flex-shrink: 0;
  min-width: 40px;
  max-width: 60px;

  // &:hover {
  //   transform: translateY(-2px);
  // }
}

.file-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

// .file-name {
//   font-size: 10px;
//   font-weight: 400;
//   color: rgba(5, 32, 49, 1);
//   text-align: center;
//   // overflow: hidden;
//   text-overflow: ellipsis;
//   white-space: nowrap;
//   word-break: keep-all;
// }
</style>
